<template>
  <div>
    <v-card>
      <Subheader no-search title="Details">
        <v-btn light @click="showSetAsAliasDialog()">
          <v-icon color="warning" class="mr-1">call_merge</v-icon>Demote to Alias
        </v-btn>
      </Subheader>
      <v-card-text>
        <v-subheader one-line class="subheading">
          <div class="primary--text" style="width:200px"><strong>Analyte Name:</strong></div>
          <div v-if="!isEdit">{{ analyteData ? analyteData.AnalyteName : '' }}</div>
          <v-text-field v-if="isEdit" v-model="editAnalyte.AnalyteName"></v-text-field>
        </v-subheader>
        <v-subheader one-line class="subheading">
          <div class="primary--text" style="width:200px"><strong>Display Name:</strong></div>
          <div v-if="!isEdit">{{ analyteData ? analyteData.DisplayName : '' }}</div>
          <v-text-field v-if="isEdit" v-model="editAnalyte.DisplayName"></v-text-field>
        </v-subheader>
        <v-subheader class="subheading">
          <div class="primary--text" style="width:200px"><strong>Applicable Unit Group:</strong></div>
          <div v-if="!isEdit">{{ analyteData ? unitGroupNameById : '' }}
          </div>
          <v-autocomplete v-model="unitGroup" v-if="isEdit" :items="allUnitGroups" item-text="Name" @change="changeUnitGroup" return-object></v-autocomplete>
        </v-subheader>
        <v-subheader class="subheading">
          <div class="primary--text" style="width:200px"><strong>Default Unit:</strong></div>
          <div v-if="!isEdit">{{ analyteData ? unitNameById : '' }}</div>
          <v-autocomplete v-model="unit" v-if="isEdit" :items="unitGroup ? unitGroup.Units : []" item-text="Name" return-object></v-autocomplete>
        </v-subheader>
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="!isEdit" @click="edit(analyteData)">
          <v-icon color="warning" class="mr-1">edit</v-icon>{{ $t('Button.Edit') }}
        </v-btn>
        <v-btn color='success' v-if="isEdit" @click="checkUnitGroup()">
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn v-if="isEdit" @click="cancel()">{{ $t('Button.Cancel') }}</v-btn>
      </v-card-actions>
    </v-card>

    <AppModal v-model="linkToGlobalRequestDialog" persistent max-width="800">
      <template v-slot:title>
        {{ $t('AnalyteDetailInformation.titleMsg1', { msg: linkToGlobalRequestParameter.AnalyteName }) }}
      </template>
      <template v-slot:text>
        <p>
          {{ $t('AnalyteDetailInformation.alertMsg') }}
        </p>
        <v-text-field v-if="ableToSetAsAlias" readonly label="Alias name" v-model="linkToGlobalRequestParameter.AnalyteName"></v-text-field>
        <v-text-field v-if="ableToSetAsAlias" label="Method" v-model="method"></v-text-field>
        <v-autocomplete :items="analyteListExceptByAnalyte" label="Target analyte" item-text="AnalyteName" return-object v-model="linkToGlobalRequestParameter.TargetAnalyte"></v-autocomplete>
        <!-- <v-text-field label="Note:" v-model="linkToGlobalRequestParameter.Note"></v-text-field> -->
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="success" @click="submitLinkToGlobalRequest()" dark>
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Submit') }}
        </v-btn>
        <v-btn @click="closeDialog()">{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <AppConfirmationModal v-model="differentUnitGroupModel" persistent max-width="800">
      <template v-slot:title>
        {{ $t('AnalyteDetailInformation.differentTitle', {link: pluralize('link', differentStandards.length)})}}
      </template>
      <template v-slot:text>
        <v-alert v-model="differentUnitGroupModel" type="warning" outline>
          {{ $t('AnalyteDetailInformation.differentMsg', {msg: differentStandardsName, link: pluralize('link', differentStandards.length), guideline: pluralize('guideline', differentStandards.length)}) }}
        </v-alert>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="success" @click="save()" dark>
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Update_Anyway') }}
        </v-btn>
        <v-btn @click="cancel();differentUnitGroupModel=false">{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppConfirmationModal>

    <AppConfirmationModal v-model="affectAnalyteCofactorModel" persistent max-width="800">
      <template v-slot:title>
        {{ $t('AnalyteDetailInformation.affectAnalyteCofactorTitle') }}
      </template>
      <template v-slot:text>
        <v-alert v-model="affectAnalyteCofactorModel" type="warning" outline>
          {{ $t('AnalyteDetailInformation.affectAnalyteCofactorMsg', {msg: affectAnalyteCofactorMapping, link: pluralize('link', affectAnalyteCofactor.length), cofactor: pluralize('cofactor', affectAnalyteCofactor.length)}) }}
        </v-alert>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="success" @click="save()" dark>
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Update_Anyway') }}
        </v-btn>
        <v-btn @click="cancel();affectAnalyteCofactorModel=false">{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppConfirmationModal>

    <v-card>
      <v-snackbar :color="snackColor" top v-model="snackbar" auto-height>
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>
  </div>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import AppModal from '@/components/generic/AppModal'
  import AppConfirmationModal from '@/components/generic/AppConfirmationModal'
  import {
    sortObject,
  } from '@/helpers/sorting'
  import {
    pluralize,
  } from '@/helpers/strings'
  import { checkUnitNamesEqual } from '@/store/unit'

  const unit = createNamespacedHelpers('unit')
  const analyte = createNamespacedHelpers('analyte')
  const authenticate = createNamespacedHelpers('authenticate')
  const guidelines = createNamespacedHelpers('guidelines')
  const cofactor = createNamespacedHelpers('cofactor', 'cofactorLinkAnalyte')

  import Subheader from '@/components/generic/Subheader'

  export default {
    components: {
      AppModal,
      AppConfirmationModal,
      Subheader,
    },
    props: {
      analyteData: {
        type: Object,
        default: () => {
          return {
            AnalyteName: '',
          }
        },
      },
      analyteGuidelines: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        isEdit: false,
        method: '',
        unitGroup: {},
        unit: {},
        differentUnitGroupModel: false,
        differentStandardsName: '',
        differentStandards: [],
        affectAnalyteCofactorModel: false,
        affectAnalyteCofactorMapping: '',
        affectAnalyteCofactor: [],
        linkToGlobalRequestDialog: false,
        linkToGlobalRequestParameter: {},
        analyteListExceptByAnalyte: [],
        editAnalyte: {
          Id: '',
          AnalyteName: '',
        },
        snackColor: '',
        snackMessage: '',
        snackbar: false,
      }
    },
    mounted() {},
    computed: {
      ...unit.mapGetters(['allUnits', 'allUnitGroups']),
      ...analyte.mapGetters(['tenantAnalyteList']),
      ...authenticate.mapGetters(['currentUserGroup']),
      ...guidelines.mapGetters(['Guidelines']),
      ...cofactor.mapGetters(['cofactorList', 'cofactorLinkAnalyte']),
      ableToSetAsAlias() {
        if (this.analyteData.Aliases) {
          if (this.analyteData.Aliases.length == 0) {
            return true
          }
          return false
        }
      },
      allAnalytes() {
        return JSON.parse(JSON.stringify(this.tenantAnalyteList))
      },
      unitGroupNameById() {
        let matched = this.allUnitGroups.find(x => x.Id == this.analyteData.ApplicableUnitGroupId)
        if (matched) {
          return matched.Name
        }
        return ''
      },
      unitNameById() {
        let matched = this.allUnits.find(x => x.Id == this.analyteData.DefaultUnitId)
        if (matched) {
          return matched.Name
        }
        return ''
      },
    },
    watch: {
      unitGroup() {
        if (!this.unitGroup && !this.unit) {
          if (!this.unitGroup.Units.find(x => x.Id == this.unit.Id)) {
            this.unit = {}
          }
        }
      },
    },
    methods: {
      ...analyte.mapActions(['setTenantAnalyteAsAlias', 'editAnalyteName']),
      pluralize,
      getAnalyteListExceptByAnalyte(analyteId) {
        return this.tenantAnalyteList.filter(x => x.Id != analyteId)
      },
      showSetAsAliasDialog() {
        let sourceAlias = this.analyteData
        this.analyteListExceptByAnalyte = this.getAnalyteListExceptByAnalyte(sourceAlias.Id)
        this.analyteListExceptByAnalyte = this.analyteListExceptByAnalyte.sort(sortObject('AnalyteName'))
        this.linkToGlobalRequestParameter.AnalyteName = sourceAlias.AnalyteName
        this.linkToGlobalRequestDialog = true
      },
      submitLinkToGlobalRequest() {
        if (this.linkToGlobalRequestParameter.TargetAnalyte) {
          if (this.ableToSetAsAlias && !this.method) {
            this.enableSnackbar('info', this.$t('AnalyteDetailInformation.nullMsg1'))
          } else {
            let alias = {
              Id: null,
              AliasName: this.analyteData.AnalyteName,
              Method: this.method ? this.method : '',
              AnalyteId: this.analyteData.Id,
              TenantId: this.currentUserGroup.id,
            }
            let options = {
              data: {
                sourceAnalyte: this.analyteData,
                targetAnalyte: this.linkToGlobalRequestParameter.TargetAnalyte,
                newAlias: this.method ? alias : null,
                // Note: this.linkToGlobalRequestParameter.Note,
              },
              successCb: () => {
                this.linkToGlobalRequestDialog = false
                this.method = ''
                this.$router.replace({
                  name: 'AnalyteDetailManage',
                  params: {
                    analyteId: options.data.targetAnalyte.Id.toString(),
                  },
                })
                this.enableSnackbar('success', this.$t('AnalyteDetailInformation.successSaveMsg'))
              },
              failedCb: (err) => {
                this.enableSnackbar('error', err.response.data.ExceptionMessage)
              },
            }
            this.setTenantAnalyteAsAlias(options)
          }
        } else {
          this.enableSnackbar('info', this.$t('AnalyteDetailInformation.nullLinkMsg'))
        }
      },
      closeDialog() {
        this.linkToGlobalRequestDialog = false
        this.method = ''
      },
      edit(data) {
        this.isEdit = !this.isEdit
        this.empty()
        this.editAnalyte.AnalyteName = data.AnalyteName
        this.editAnalyte.DisplayName = data.DisplayName
        this.editAnalyte.Id = data.Id
        this.unitGroup = this.allUnitGroups.find(x => x.Id == data.ApplicableUnitGroupId)
        if (this.unitGroup) {
          this.unit = this.unitGroup.Units.find(x => x.Id == data.DefaultUnitId)
        } else {
          this.unit = {}
        }
      },
      changeUnitGroup() {
        this.unit = {}
      },
      checkUnitGroup() {
        if (this.unitGroup && 'Id' in this.unitGroup) {
          let differentStandards = []
          let targetUnitGroupUnits = this.allUnitGroups.find(x => x.Id == this.unitGroup.Id).Units.map(x => x.Name)
          this.analyteGuidelines.forEach(x => {
            if (!targetUnitGroupUnits.find(y => checkUnitNamesEqual(y, x.Unit))) {
              differentStandards.push(x)
            }
          })

          if (differentStandards.length) {
            this.differentStandards = differentStandards.map(x => x.GuidelineName)
            this.differentStandardsName = this.differentStandards.join(', ')
            this.differentUnitGroupModel = true
            return
          }
          var cofactorAnalyteMap = this.cofactorLinkAnalyte.filter(x => x.AnalyteId == this.analyteData.Id)
          let cofactorMismatchedUnitGroups = []
          if (cofactorAnalyteMap && cofactorAnalyteMap.length) {
            cofactorAnalyteMap.forEach(element => {
              let linkedCofactor = this.cofactorList.find(x => x.Id == element.CoFactorId)
              if (linkedCofactor.UnitGroups.every(x => x.Id != this.unitGroup.Id)) {
                cofactorMismatchedUnitGroups.push(linkedCofactor)
              }
            })
            if (cofactorMismatchedUnitGroups.length) {
              this.affectAnalyteCofactor = cofactorMismatchedUnitGroups.map(x => x.Name)
              this.affectAnalyteCofactorMapping = this.affectAnalyteCofactor.join(', ')
              this.affectAnalyteCofactorModel = true
              return
            }
          }
        }  
        this.save()
      },
      save() {
        this.isEdit = !this.isEdit
        let analyteNames = this.allAnalytes.map(e => e.AnalyteName)
        analyteNames = Array.from(new Set(analyteNames))
        let self = analyteNames.indexOf(this.analyteData.AnalyteName)
        if (self > -1) {
          analyteNames.splice(self, 1)
        }
        let repeat = false
        if (this.editAnalyte.AnalyteName) {
          analyteNames.forEach(a => {
            if (a == this.editAnalyte.AnalyteName) {
              repeat = true
            }
          })
          if (repeat) {
            this.enableSnackbar('info', this.$t('AnalyteManage.repeatMsg'))
          } else {
            let options = {
              data: {
                Id: this.editAnalyte.Id,
                AnalyteName: this.editAnalyte.AnalyteName,
                DisplayName: this.editAnalyte.DisplayName,
                DefaultUnitId: this.unit ? this.unit.Id : null,
                ApplicableUnitGroupId: this.unitGroup ? this.unitGroup.Id : null,
                TenantId: this.currentUserGroup.id,
              },
              successCb: () => {
                this.enableSnackbar('success', this.$t('AnalyteDetailInformation.successSaveMsg1'))
                this.empty()
                this.differentUnitGroupModel = false
                this.affectAnalyteCofactorModel = false
              },
              failedCb: err => {
                this.enableSnackbar('error', err)
                this.differentUnitGroupModel = false
                this.affectAnalyteCofactorModel = false
              },
            }
            this.editAnalyteName(options)
          }
        } else {
          this.enableSnackbar('info', this.$t('AnalyteDetailInformation.nullMsg'))
        }
      },
      cancel() {
        this.isEdit = false
        this.unitGroup = {}
        this.unit = {}
      },
      empty() {
        this.editAnalyte = {
          Id: '',
          AnalyteName: '',
        }
        this.method = ''
      },
      enableSnackbar(color, message) {
        this.snackbar = true
        this.snackColor = color
        this.snackMessage = message
      },
    },
  }
</script>

<style lang="stylus" scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }
</style>